/**
 * CLIENT EMAILS AS A LIST (ops #76 O6, `docs/plan-o6-o9-emails.md` section 3.7, "What the office sees"):
 * the rows the invoice screen's Emails card and the household's Emails card both draw, so one email
 * reads the same in both places. Each says what it was, who it went to with her address masked, when
 * it was written, its subject where the read carries one, and where it stands in the one helper's words
 * (`emailStanding`). Coral where somebody has something to do: it did not go, it bounced, the register
 * refused it, or ops held it; sage once delivered.
 *
 * Words only, as text and never as markup. Nothing to press: ops composes and records, home delivers
 * (binding rule 3), and the office's ways on are where they always were, the call, the invoice's
 * "Send the invoice", her record. One about an invoice opens it from the household's card.
 *
 * On the invoice screen, another invoice's past-due note that names this one in its "also past due"
 * sentence says so, with the number of the invoice it is for (`namedInWords`).
 *
 * At a phone's width every part wraps: the kind and its pill share a line while they fit, a long
 * subject breaks inside itself, and the sentence is a paragraph of its own.
 */
import { Link } from 'react-router-dom';
import { humanise } from '@fk/shared';
import type { OfficeEmailRow } from '../api.ts';
import { formatInstant } from '../format.ts';
import { EMAIL_KIND_LABELS, type EmailStanding } from '../labels.ts';
import { emailStanding } from '../screens/visit-detail/shared.ts';
import { Pill } from './AppShell.tsx';

/** The states somebody at the office acts on, drawn coral: a gap, not a fact. */
const NEEDS_SOMEBODY: ReadonlySet<EmailStanding> = new Set<EmailStanding>([
  'did_not_go',
  'bounced_late',
  'refused',
  'held_by_switch',
  'held_words',
]);

function toneOf(standing: EmailStanding): 'coral' | 'sage' | 'quiet' {
  if (NEEDS_SOMEBODY.has(standing)) return 'coral';
  return standing === 'delivered' ? 'sage' : 'quiet';
}

/** What the office calls a kind it knows, and a kind a later Worker added in plain words. */
const kindWords = (kind: string): string =>
  (EMAIL_KIND_LABELS as Readonly<Record<string, string>>)[kind] ?? humanise(kind);

/**
 * WHAT AN EMAIL ABOUT ANOTHER INVOICE THAT NAMES THIS ONE IS, in the office's words, or null for one
 * that does not: a past-due note, the one kind whose words name an invoice beside its own.
 */
function namedInWords(email: OfficeEmailRow): string | null {
  if (!email.namesIt) return null;
  const which = email.invoiceNumber ?? 'another invoice';
  return email.kind === 'invoice_overdue'
    ? `Named in the past-due note for ${which}.`
    : `Named in the email about ${which}.`;
}

export function EmailRows({
  emails,
  invoiceLinks = false,
}: {
  emails: readonly OfficeEmailRow[];
  /** Offer the invoice an email is about, from a page that is not that invoice's. */
  invoiceLinks?: boolean;
}) {
  return (
    <ul className="divide-y divide-navy/10">
      {emails.map((email) => {
        const standing = emailStanding(email);
        const named = namedInWords(email);
        return (
          <li key={email.id} className="py-2 text-sm" data-email={email.id}>
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
              <span className="font-medium">{kindWords(email.kind)}</span>
              <Pill tone={toneOf(standing.standing)}>{standing.label}</Pill>
            </div>
            <p className="mt-0.5 text-xs text-navy/60">
              {`To ${email.contactName}${email.toMasked ? `, ${email.toMasked}` : ''}, written ${formatInstant(email.createdAt) ?? email.createdAt}`}
            </p>
            {email.subject ? (
              <p className="mt-1 break-words text-navy/80">{email.subject}</p>
            ) : null}
            {named ? <p className="mt-1 text-navy/80">{named}</p> : null}
            <p className="mt-1">{standing.sentence}</p>
            {invoiceLinks && email.invoiceId ? (
              <Link
                to={`/invoices/${email.invoiceId}`}
                className="mt-1 inline-flex min-h-11 desk:min-h-9 items-center text-sm font-medium underline"
              >
                The invoice
              </Link>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
